// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { filterVisibleVocabulary } from "../consts/hiddenVocabulary";
import { kanaToRomaji } from "./kanaToRomaji";
import { stripRomajiApostrophes } from "./romajiApostrophe";

// Every entry of a level file, hidden or not, and the visible list derived from
// it. Both are cached per level and language, so a repeat call returns the same
// promise.
const levelPromiseCache = {};
const visibleLevelPromiseCache = {};
let kanjiExampleMeaningsPromise = null;

function fetchJson(path, label) {
    return fetch(`${process.env.PUBLIC_URL}${path}`).then((res) => {
        if (!res.ok) throw new Error(`Failed to load ${label} (status ${res.status})`);
        return res.json();
    });
}

// Each JLPT level's word list is a separate static JSON file (built from
// JMdict + a JLPT tag list), fetched on demand and cached in memory so
// switching levels back and forth doesn't re-fetch. PT-PT meanings live in
// a parallel per-level file keyed by word id, fetched only for PT pages.
// This is the whole file, including the N5/N4 entries the app does not show
// (src/consts/hiddenVocabulary.ts); only code that must still resolve a hidden
// entry (saved words) should read it. Pages use fetchVocabularyLevel.
export function fetchFullVocabularyLevel(level, language = "en") {
    const isPt = language === "pt";
    const key = `${level}-${isPt ? "pt" : "en"}`;
    if (!levelPromiseCache[key]) {
        const english = fetchJson(`/vocab-data/n${level}.json`, `N${level} vocabulary`);
        levelPromiseCache[key] = isPt
            ? Promise.all([english, fetchJson(`/vocab-data/pt/n${level}.json`, `N${level} PT vocabulary`)]).then(
                  ([words, ptMeanings]) =>
                      words.map((word) => ({
                          ...word,
                          englishMeanings: word.meanings,
                          meanings: ptMeanings[word.id] ?? word.meanings,
                      }))
              )
            : english;
        levelPromiseCache[key].catch(() => {
            delete levelPromiseCache[key];
        });
    }
    return levelPromiseCache[key];
}

// The list the app shows: the level file without the hidden N5/N4 entries
// (NIH-282). This is the one seam every list consumer goes through, so the
// Vocabulary page, quizzes, Verb and Adjective Conjugation, the worksheet verb
// picker and Speaking Practice all inherit the rule. N3 to N1 are returned
// whole, and ids are never changed.
export function fetchVocabularyLevel(level, language = "en") {
    const key = `${level}-${language === "pt" ? "pt" : "en"}`;
    if (!visibleLevelPromiseCache[key]) {
        visibleLevelPromiseCache[key] = fetchFullVocabularyLevel(level, language).then((words) =>
            filterVisibleVocabulary(words, level)
        );
        visibleLevelPromiseCache[key].catch(() => {
            delete visibleLevelPromiseCache[key];
        });
    }
    return visibleLevelPromiseCache[key];
}

export function wordMatchesMeaning(word, query) {
    return [...(word.meanings || []), ...(word.englishMeanings || [])].some((meaning) =>
        meaning.toLowerCase().includes(query)
    );
}

// Cards show each word's reading with its romaji, e.g. "おくれる (okureru)",
// so learners search by romaji too. The query is expected to be lower-cased
// already, like wordMatchesMeaning's. The romaji of 翻訳 is "hon'yaku" but
// "honyaku" finds it too, and so does typing the apostrophe: it is dropped from
// both sides.
export function wordMatchesRomaji(word, query) {
    return stripRomajiApostrophes(kanaToRomaji(word.reading)).includes(stripRomajiApostrophes(query));
}

export function fetchKanjiExampleMeaningsPt() {
    if (!kanjiExampleMeaningsPromise) {
        kanjiExampleMeaningsPromise = fetchJson("/vocab-data/pt/kanji-examples.json", "PT kanji examples");
        kanjiExampleMeaningsPromise.catch(() => {
            kanjiExampleMeaningsPromise = null;
        });
    }
    return kanjiExampleMeaningsPromise;
}
